import { css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import React from "react";
import NoomLogo from "./images/noom_logo_symbol_orange.svg";
import { flexMiddle } from "src/design-system/styles/layout";
import { compassColors } from "src/utils/styling";

type Dimensions = { size?: number };

type Props = Dimensions & {
  animate?: boolean;
  className?: string;
  fill?: string;
  fullScreen?: boolean;
};

type LogoProps = Dimensions & {
  fill: string;
  keyframe: ReturnType<typeof keyframes>;
};

const edgeState = css`
  transform: rotate(0deg);
  opacity: 1;
`;

const rotateImage = keyframes`
  0% { ${edgeState}; }
  50% {
    transform: rotate(90deg);
    opacity: 0.85;
  }
  100% { ${edgeState}; }
`;

const Container = styled.div<Dimensions & { fullScreen?: boolean }>`
  ${flexMiddle};
  position: relative;
  ${({ fullScreen }) =>
    fullScreen &&
    css`
      margin: 0 auto;
      min-height: 100vh;
    `}

  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`;

const LogoContainer = styled.span<Omit<LogoProps, "fill">>`
  position: absolute;

  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;

  ${({ keyframe }) =>
    keyframe &&
    css`
      animation: 4s infinite cubic-bezier(0.46, 0.03, 0.52, 0.96) ${keyframe};
    `}
`;

export function NoomLoader({
  className,
  size = 100,
  animate = true,
  fullScreen = false,
  fill = compassColors.blueberry,
}: Props) {
  return (
    <Container className={className} size={size} fullScreen={fullScreen}>
      {animate ? (
        <AnimationDiamond size={size} fill={fill} keyframe={rotateImage} />
      ) : (
        <StaticLogo size={size} />
      )}
    </Container>
  );
}

function StaticLogo({ size }: Dimensions) {
  return <img src={NoomLogo} width={size} height={size} alt="Noom Logo" />;
}

function AnimationDiamond({ size, fill, keyframe }: LogoProps) {
  return (
    <LogoContainer size={size} keyframe={keyframe}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 44 43"
        fill={fill}
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M31.656 12.064C25.32 5.874 22.005.008 22 0c-.005.009-3.322 5.874-9.656 12.064C6.011 18.253.008 21.495 0 21.5c.008.005 6.01 3.246 12.344 9.436C18.68 37.126 21.995 42.992 22 43c.005-.008 3.322-5.874 9.656-12.064C37.989 24.746 43.992 21.505 44 21.5c-.008-.005-6.01-3.246-12.344-9.436z" />
      </svg>
    </LogoContainer>
  );
}
